import { fromJS } from 'immutable';

import { claimsErrorState, GET_AAPI_USER_SUCCESS } from 'Containers/App/constants';
import { AlexIdUser } from 'Containers/App/types';
import { stripSourceComponent } from 'Containers/App/utils';
import { GET_RECOMMENDATIONS_SUCCESS, SET_SELECTED_MEMBER_IDS } from 'Containers/CommercialRoutes/constants';
import {
  determineHiddenEqs,
  isMemberComplete,
  newMemberState,
  newMemberStateWithExistingExternalId,
  parseClaimsSummary,
  validateMember,
} from 'Containers/ProfilePage/utils';
import { Household, HouseholdMember } from 'Types/entities';
import {
  ClientSurveySchema,
  Election,
  ElectionMember,
  GetBatchDrugDetailsResponse,
  GetDrugsResponse,
  GetLocationsResponse,
  IncentiveSurveyResponse,
  NetworkPreferenceSurveyResponse,
  ItemReference,
  JVPDependent,
  JVPEligibilityQuestions,
  JVPSexAtBirth,
  JVPUserWithDependents,
} from 'Utils/apiTypes';
import Logger from 'Utils/logger';
import { wrapImmutableReducerInPojoTranslation } from 'Utils/reducers';

import {
  CHANGE_FORM_VALUE,
  CHANGE_SURVEY_VALUE,
  CHANGE_SURVEY_VALUE_FROM_RESULT_PAGE,
  CLAIMS_SUMMARY_POLLING_STOPPED,
  CREATE_OR_UPDATE_HOUSEHOLD_FAILURE,
  CREATE_OR_UPDATE_HOUSEHOLD_REQUEST,
  CREATE_OR_UPDATE_HOUSEHOLD_SUCCESS,
  EDIT_DEPENDENT,
  EDIT_POLICYHOLDER,
  EDIT_SPOUSE,
  GET_CLIENT_SURVEY_FAILURE,
  GET_CLIENT_SURVEY_REQUEST,
  GET_CLIENT_SURVEY_SUCCESS,
  GET_HOUSEHOLD_SUCCESS,
  GET_INCENTIVE_SURVEY_FAILURE,
  GET_INCENTIVE_SURVEY_SUCCESS,
  GET_JVP_CLIENT_SURVEY_FAILURE,
  GET_JVP_CLIENT_SURVEY_REQUEST,
  GET_JVP_CLIENT_SURVEY_SUCCESS,
  GET_JVP_ELIGIBILITY_QUESTIONS_FAILURE,
  GET_JVP_ELIGIBILITY_QUESTIONS_SUCCESS,
  GET_LATEST_CLAIMS_SUMMARY_FAILURE,
  GET_LATEST_CLAIMS_SUMMARY_REQUEST,
  GET_LATEST_CLAIMS_SUMMARY_SUCCESS,
  PREFILL_USER_PROFILE_SUCCESS,
  REMOVE_DEPENDENT,
  REMOVE_SPOUSE,
  RESET_COUNTY_RESULTS,
  SANITIZE_PROFILE_INPUTS,
  SEARCH_CLAIMS_PAYERS_FAILURE,
  SEARCH_CLAIMS_PAYERS_REQUEST,
  SEARCH_CLAIMS_PAYERS_SUCCESS,
  SEARCH_DRUGS_FAILURE,
  SEARCH_DRUGS_REQUEST,
  SEARCH_DRUGS_SUCCESS,
  CLEAR_INCENTIVE_LOADING,
  SEARCH_LOCATIONS_FAILURE,
  SEARCH_LOCATIONS_REQUEST,
  SEARCH_LOCATIONS_SUCCESS,
  SET_ACTIVE_HOUSEHOLD_MEMBERS,
  SET_INCENTIVE_LOADING,
  SET_INCENTIVE_SURVEY,
  SET_RESULT_PAGE_INCENTIVE_SURVEY,
  STANDARD_EQ_TO_QUESTION_CODE,
  SUBMIT_CLAIMS_FORM,
  SUBMIT_CLAIMS_FORM_FAILURE,
  SUBMIT_CLAIMS_FORM_SUCCESS,
  SUBMIT_CLIENT_SURVEY,
  SUBMIT_INCENTIVE_SURVEY,
  SUBMIT_RESULT_INCENTIVE_SURVEY,
  GET_NETWORK_PREFERENCE_SURVEY_SUCCESS,
  GET_NETWORK_PREFERENCE_SURVEY_FAILURE,
  SUBMIT_NETWORK_PREFERENCE_SURVEY,
  SurveyQuestionMap,
  UPDATE_CLAIMS_FORM_DATA,
  UPDATE_CLAIMS_FORM_STATUS,
  UPDATE_HOUSEHOLD_FROM_RESULT_PAGE,
} from './constants';
import { HouseholdTrackingProfileState } from './HouseholdTrackingState';
import { initialProfileState, IProfilePageReducerState } from './state';
import {
  CapacityToPayAnswer,
  ClaimsFormStatus,
  ClientSurveyResponsesByYear,
  IncentiveSurveyAnswer,
  NetworkPreferenceSurveyAnswers,
  Member,
  MEMBER_TYPE,
  MemberSectionConfigFields,
  NotFoundError,
  Prescription,
  RiskAssessmentAnswer,
  Survey,
  TimeoutError,
  UpdateMemberFields,
} from './types';

function profilePageReducer(state: HouseholdTrackingProfileState, action): HouseholdTrackingProfileState {
  switch (action.type) {
    case CREATE_OR_UPDATE_HOUSEHOLD_SUCCESS:
      return createOrUpdateHouseholdSuccess(state, action);
    case GET_HOUSEHOLD_SUCCESS:
      return populateHousehold(state, action);
    case PREFILL_USER_PROFILE_SUCCESS:
      return prefillUserProfile(state, action);
    case GET_CLIENT_SURVEY_REQUEST:
      return getClientSurvey(state);
    case GET_CLIENT_SURVEY_SUCCESS:
      return getClientSurveySuccess(state, action);
    case GET_CLIENT_SURVEY_FAILURE:
      return getClientSurveyFailure(state, action);
    case GET_JVP_CLIENT_SURVEY_REQUEST:
      return getJvpClientSurvey(state);
    case GET_JVP_CLIENT_SURVEY_SUCCESS:
      return getJvpClientSurveySuccess(state, action);
    case GET_JVP_CLIENT_SURVEY_FAILURE:
      return getJvpClientSurveyFailure(state, action);
    case SUBMIT_CLIENT_SURVEY:
      return submitClientSurvey(state, action);
    case SUBMIT_INCENTIVE_SURVEY:
      return submitIncentiveSurvey(state, action);
    case SUBMIT_RESULT_INCENTIVE_SURVEY:
      return submitResultIncentiveSurvey(state, action);
    case SUBMIT_NETWORK_PREFERENCE_SURVEY:
      return submitNetworkPreferenceSurvey(state, action);
    case GET_NETWORK_PREFERENCE_SURVEY_SUCCESS:
      return getNetworkPreferenceSurveySuccess(state, action);
    case GET_NETWORK_PREFERENCE_SURVEY_FAILURE:
      return getNetworkPreferenceSurveyFailure(state, action);
    case EDIT_POLICYHOLDER:
      return editPolicyholder(state, action);
    case EDIT_SPOUSE:
      return editSpouse(state, action);
    case EDIT_DEPENDENT:
      return editDependent(state, action);
    case REMOVE_SPOUSE:
      return removeSpouse(state);
    case REMOVE_DEPENDENT:
      return removeDependent(state, action);
    case CHANGE_FORM_VALUE:
      return changeFormValue(state, action);
    case CHANGE_SURVEY_VALUE:
      return changeSurveyValue(state, action);
    case CHANGE_SURVEY_VALUE_FROM_RESULT_PAGE:
      return changeSurveyValue(state, action);
    case SEARCH_LOCATIONS_REQUEST:
      return searchLocations(state);
    case SEARCH_LOCATIONS_SUCCESS:
      return searchLocationsSuccess(state, action);
    case SEARCH_LOCATIONS_FAILURE:
      return searchLocationsFailure(state, action);
    case RESET_COUNTY_RESULTS:
      return resetCounties(state);
    case SEARCH_DRUGS_REQUEST:
      return searchDrugs(state, action);
    case SEARCH_DRUGS_SUCCESS:
      return searchDrugsSuccess(state, action);
    case SEARCH_DRUGS_FAILURE:
      return searchDrugsFailure(state, action);
    case SANITIZE_PROFILE_INPUTS:
      return sanitizeProfileInputs(state);
    case SET_SELECTED_MEMBER_IDS:
      return setSelectedMemberIds(state, action);
    case GET_AAPI_USER_SUCCESS:
      return handleGetAAPIUserSuccess(state, action);
    case GET_INCENTIVE_SURVEY_SUCCESS:
      return getIncentiveSurveySuccess(state, action);
    case SET_INCENTIVE_SURVEY:
      return setIncentiveSurvey(state, action);
    case GET_INCENTIVE_SURVEY_FAILURE:
      return getIncentiveSurveyFailure(state, action);
    case CREATE_OR_UPDATE_HOUSEHOLD_REQUEST:
    case UPDATE_HOUSEHOLD_FROM_RESULT_PAGE:
      return createHouseholdRequest(state);
    case CREATE_OR_UPDATE_HOUSEHOLD_FAILURE:
      return state.set('incentiveSurveyIsLoading', false).set('householdDataChanged', false);
    case SET_INCENTIVE_LOADING:
      return state.set('incentiveSurveyIsLoading', true);
    case CLEAR_INCENTIVE_LOADING:
      return state.set('incentiveSurveyIsLoading', false);
    case SET_ACTIVE_HOUSEHOLD_MEMBERS:
      return setActiveHouseholdMembers(state, action);
    case SET_RESULT_PAGE_INCENTIVE_SURVEY:
      return setResultPageIncentiveSurvey(state, action);
    case GET_JVP_ELIGIBILITY_QUESTIONS_SUCCESS:
      return getJvpEligibilityQuestionsSuccess(state, action);
    case GET_JVP_ELIGIBILITY_QUESTIONS_FAILURE:
      return getJvpEligibilityQuestionsFailure(state, action);
    case GET_RECOMMENDATIONS_SUCCESS:
      return state.set('incentiveSurveyIsLoading', false);
    /** * Claims Actions ** */
    case UPDATE_CLAIMS_FORM_DATA:
      return state.setIn(['claimsForm', action.field], action.value);
    case UPDATE_CLAIMS_FORM_STATUS:
      return state.set('claimsFormStatus', action.status);
    case SEARCH_CLAIMS_PAYERS_REQUEST:
      return state.set('claimsPayersQuery', action.query).set('claimsPayersLoading', true);
    case SEARCH_CLAIMS_PAYERS_SUCCESS:
      return state.set('claimsPayers', fromJS(action.response.payers)).set('claimsPayersLoading', false);
    case SEARCH_CLAIMS_PAYERS_FAILURE:
      return state.set('claimsPayersLoading', false).set('claimsError', getClaimsSummaryError(action.error));
    case SUBMIT_CLAIMS_FORM:
      return state
        .set('claimsFormStatus', ClaimsFormStatus.Loading)
        .set('claimsSummary', null)
        .set('claimsError', null);
    case SUBMIT_CLAIMS_FORM_SUCCESS:
      return state
        .set('claimsFormStatus', ClaimsFormStatus.Submitted)
        .set('claimsSummaryId', action.response.id);
    case SUBMIT_CLAIMS_FORM_FAILURE:
      return state
        .set('claimsFormStatus', ClaimsFormStatus.Failure)
        .set('claimsError', getClaimsSummaryError(action.error));
    case GET_LATEST_CLAIMS_SUMMARY_REQUEST:
      return state.set('claimsSummaryPolling', true);
    case GET_LATEST_CLAIMS_SUMMARY_SUCCESS: {
      if (claimsErrorState.includes(action.response?.claims_summary?.status)) {
        return state
          .set('claimsErrorState', action.response?.claims_summary?.status)
          .set('claimsSummaryPolling', false)
          .set('claimsSummary', null)
          .set('claimsError', null)
          .set('claimsFormStatus', ClaimsFormStatus.Failure);
      }
      const attemptedParse = parseClaimsSummary(action.response?.claims_summary);
      if (attemptedParse instanceof Error) {
        return state
          .set('claimsSummaryPolling', false)
          .set('claimsSummary', null)
          .set('claimsError', getClaimsSummaryError(attemptedParse))
          .set('claimsFormStatus', ClaimsFormStatus.Failure);
      }
      return state
        .set('claimsSummary', fromJS(attemptedParse))
        .set('claimsSummaryPolling', false)
        .set('claimsError', null)
        .set('claimsFormStatus', ClaimsFormStatus.Success)
        .set('claimsErrorState', null);
    }
    case GET_LATEST_CLAIMS_SUMMARY_FAILURE:
      return state
        .set('claimsFormStatus', ClaimsFormStatus.Failure)
        .set('claimsError', getClaimsSummaryError(action.error));
    case CLAIMS_SUMMARY_POLLING_STOPPED:
      return state.set('claimsSummaryPolling', false);
    default:
      return state;
  }
}

function createOrUpdateHouseholdSuccess(
  state: HouseholdTrackingProfileState,
  action: {
    response: ItemReference;
    builderCustomerKey?: string;
  },
) {
  localStorage.setItem('household_id', action.response.household_id);

  const eligibleEnrollmentPlan = action.response.eligible_enrollment_plan ?? null;
  const builderCustomerKey = action.builderCustomerKey;
  if (builderCustomerKey) {
    return state
      .set('householdIsLoaded', true)
      .set('household_id', action.response.household_id)
      .set('eligible_enrollment_plan', eligibleEnrollmentPlan)
      .set('householdDataChanged', false);
  }
  return state
    .set('householdIsLoaded', true)
    .set('household_id', action.response.household_id)
    .set('eligible_enrollment_plan', eligibleEnrollmentPlan)
    .set('incentiveSurveyIsLoading', false)
    .set('householdDataChanged', false);
}

function createHouseholdRequest(state: HouseholdTrackingProfileState) {
  return state.set('incentiveSurveyIsLoading', true);
}

function getClaimsSummaryError(error: Error | null): Error | null {
  if (!error) return null;

  let wrappedError: Error;
  if (error.message.includes('502')) {
    wrappedError = new NotFoundError(error);
  } else if (error.message.includes('Timeout')) {
    wrappedError = new TimeoutError(error);
  } else {
    return error;
  }

  return wrappedError;
}

/**
 *
 * @param member HouseholdMember from Comm API getHousehold
 * @param memberConfig MemberSectionConfigFields needed to validate the household member for form prefill
 * @param drugDetails Object of Drug Details present in the household from Comm API getDrugDetails, for form prefill display
 * @param income
 * @param isCovered Used to set covered members based on get_elections JVP response
 * @returns
 */
function formatMember(
  member: HouseholdMember,
  memberConfig: MemberSectionConfigFields,
  drugDetails?: GetBatchDrugDetailsResponse | null,
  income?: number | null,
  isCovered?: boolean | true,
): Member {
  const {
    member_type,
    external_id,
    age,
    gender,
    uses_tobacco,
    on_medicare,
    utilization,
    planned_procedures,
    prescriptions,
  } = member;

  const memberPrescriptions: Prescription[] = [];

  /*
    any first time user won't have utilization or planned procedures (if it's enabled) until they completely fill out the usage quiz
    and the frontend successfully calls JVP to update their records, so it's a pretty safe bet to
    initialize the profile builder form usage toggles based on the presence of existing usage data
  */
  const hasSubmittedMedQuiz = utilization && planned_procedures;
  const uses_prescriptions = hasSubmittedMedQuiz ? prescriptions?.length > 0 : null;
  const uses_utilization = hasSubmittedMedQuiz ? Object.values(utilization).some((i) => i > 0) : null;

  const uses_planned_procedures = planned_procedures
    ? Object.values(planned_procedures).some((i) => i > 0)
    : null;

  // Create expected prescription array from cache of present drugs
  if (drugDetails && uses_prescriptions) {
    member.prescriptions.forEach((prescription) => {
      const ndc = prescription.ndc;

      const rxDetail = ndc in drugDetails ? drugDetails[ndc] : null;
      if (rxDetail) {
        memberPrescriptions.push({
          ndc: rxDetail.ndc,
          displayName: rxDetail.name,
          drugForm: rxDetail.form,
        });
      }
    });
  }

  const formattedMember: Member = {
    external_id,
    member_type: member_type as MEMBER_TYPE,
    age: age?.toString() || '',
    gender: gender || '',
    uses_tobacco,
    on_medicare,
    uses_prescriptions,
    prescriptions: memberPrescriptions,
    uses_utilization,
    utilization: {
      inpatient_days: utilization?.inpatient_days ?? 0,
      specialist_visits: utilization?.specialist_visits ?? 0,
      pcp_visits: utilization?.pcp_visits ?? 0,
    },
    uses_planned_procedures,
    planned_procedures: {
      inpatient_procedures: planned_procedures?.inpatient_procedures ?? 0,
      outpatient_procedures: planned_procedures?.outpatient_procedures ?? 0,
      routine_therapy_visits: planned_procedures?.routine_therapy_visits ?? 0,
      mental_health_visits: planned_procedures?.mental_health_visits ?? 0,
    },
    income: income?.toString() || '',
    isSkipIncome: income?.toString() === '0',
    isValid: true,
    pristine: false,
    isCovered: isCovered !== false,
    isComplete: true,
  };
  // Update isValid status for household member if memberConfig is present
  // since otherwise we don't know if
  if (memberConfig) {
    formattedMember.isComplete = isMemberComplete(formattedMember, memberConfig);
    formattedMember.isValid = validateMember(formattedMember, memberConfig);
  }
  return formattedMember;
}

function populateHousehold(
  state: HouseholdTrackingProfileState,
  action: { response: Household; memberConfig: MemberSectionConfigFields },
) {
  /*
  Populates the reducer with household data and consolidates differences between
  Member objects returned from API and those of the reducer
  */

  const household = action.response;
  const memberConfig = action.memberConfig;

  const householdPolicyholder = household.members.find((member) => member.policyholder);
  const householdSpouse = household.members.find(
    (member: HouseholdMember) => !member.policyholder && !member.dependant,
  );
  const householdDependents = household.members.filter((member: HouseholdMember) => member.dependant);

  const policyholder = householdPolicyholder ? formatMember(householdPolicyholder, memberConfig) : {};

  const spouse = householdSpouse ? formatMember(householdSpouse, memberConfig) : {};

  const dependents =
    householdDependents.length > 0
      ? householdDependents.map((dependent: HouseholdMember) => formatMember(dependent, memberConfig))
      : [];

  return state
    .setRaw('householdIsLoaded', true)
    .setRaw('policyholder', fromJS({ ...state.get('policyholder').toJS(), ...policyholder }))
    .setRaw('spouse', fromJS({ ...state.get('spouse').toJS(), ...spouse }))
    .setRaw('dependents', fromJS([...dependents, newMemberState(MEMBER_TYPE.CHILD)]))
    .setRaw('payPeriodsPerYear', fromJS(household.pay_periods_per_year));
}

function jvpGenderToCommApiGender(gender: JVPSexAtBirth): string | null {
  switch (gender) {
    case 'm':
      return 'male';
    case 'f':
      return 'female';
    default:
      return gender;
  }
}

/**
 * Used for APP-1219: Prefill user profile
 * This code is nearly identical to the prefillIntegratedUserProfile code, but this one does not lock responses
 * and it does not currently fill in Eligibility (which comes in from JVP's bootstrap) as that is not in-scope
 * for APP-1219
 *
 * @param state
 * @param action { response: Household from comm API, validClientSurveyQuestionIds: list of client survey question IDs that are on the current plan year client survey,
 * publicationKey: determines which "plan year"/OE config being accessed. For a given bootstrap request, we'll get active and sometimes upcoming. Once we answer the "enrollment reason" question, we'll firmly place you in one or the other and subsequent requests should have the right versions enrollment event id,
 * jvpUser: JV Platform user. See JVPUser in utils/apiTypes }
 * @returns
 */
function prefillUserProfile(
  state: HouseholdTrackingProfileState,
  action: {
    household: Household;
    publicationKey: 'active' | 'upcoming';
    validClientSurveyQuestionIds: string[];
    jvpUser: JVPUserWithDependents;
    drugDetails: GetBatchDrugDetailsResponse | null;
    elections: Election[];
    memberConfig: MemberSectionConfigFields;
    jvpEligibilityQuestions: JVPEligibilityQuestions;
    electionMembersEnabled: boolean;
  },
) {
  const { client_survey, zip_code, members, survey, income, hsa_current_balance_toward_oop } =
    action.household;
  const {
    publicationKey,
    validClientSurveyQuestionIds,
    jvpUser,
    drugDetails,
    memberConfig,
    elections,
    jvpEligibilityQuestions,
    electionMembersEnabled,
  } = action;
  // *** Eligibility ***
  /**
   * This block of code resolves an issue where the Household response from Comm API contains
   * client survey responses with JV eligibility question IDs from previous years or configs.
   * We are validating the current EQ/Client Survey configuration state against the responses
   * from the household so we only put the answers for valid responses into Redux.
   * Having invalid EQ/CS Question IDs in the Redux form responses was resulting in 400s from JVP.
   */

  const invalidQuestionIds: string[] = [];

  if (client_survey && validClientSurveyQuestionIds?.length > 0) {
    // Remove invalid question IDs from the client_survey object
    Object.keys(client_survey).forEach((questionId) => {
      if (!validClientSurveyQuestionIds.includes(questionId)) {
        invalidQuestionIds.push(questionId);
        delete client_survey[questionId];
      }
    });

    if (invalidQuestionIds.length > 0)
      Logger.warn(`The following question IDs are invalid for this publication: ${invalidQuestionIds}`);
  }

  const clientSurveyResponses = {
    [publicationKey]: client_survey,
  };

  // Integrated EQs should have a 'question_code' defined
  const integratedEqs = jvpEligibilityQuestions[publicationKey]?.filter((q) => !!q.question_code) || [];

  const hiddenEligibilityQuestions: string[] = determineHiddenEqs(client_survey, integratedEqs);

  // *** Location ***
  const zipCode = zip_code || state.get('zipcode');
  const stateCode = action.household.state || '';

  const hiddenLocationQuestions: string[] = [];

  if (zipCode && integratedEqs.find((q) => q.question_code === STANDARD_EQ_TO_QUESTION_CODE.zipcode)) {
    hiddenLocationQuestions.push('zip5');
  }

  if (stateCode && integratedEqs.find((q) => q.question_code === STANDARD_EQ_TO_QUESTION_CODE.state)) {
    hiddenLocationQuestions.push('state');
  }

  // *** HSA Funds ***
  // HSA spend for return users; if present, user wants to use HSA ('yes'), otherwise 'no'
  const hsaFundsData = {
    hasHsaFunds:
      hsa_current_balance_toward_oop && hsa_current_balance_toward_oop > 0
        ? 'yes'
        : hsa_current_balance_toward_oop === 0
          ? 'no'
          : null, // null for unanswered (undefined/null from backend)
    hsaAmount:
      hsa_current_balance_toward_oop && hsa_current_balance_toward_oop > 0
        ? hsa_current_balance_toward_oop.toString()
        : '',
  };

  // *** Member ***
  const medicalElection: Election | undefined = elections.find(
    (election: Election) => election.planable_type === 'MedicalPlan',
  );

  // If a user is missing from members, we should default them to 'covered'.
  // Only set isCovered to false for users that we have is_covered: false from a JVP election
  let explicitlyUncoveredMemberIds: string[] = [];

  if (medicalElection && medicalElection.election_members) {
    explicitlyUncoveredMemberIds = !electionMembersEnabled
      ? []
      : medicalElection.election_members
          .filter((m: ElectionMember) => !m.is_covered && m.external_id !== null)
          .map((member: ElectionMember) => member.external_id as string);
  }

  const householdPolicyholder = members.find((member) => member.policyholder);

  if (householdPolicyholder && jvpUser) {
    householdPolicyholder.gender = jvpGenderToCommApiGender(jvpUser.sex_at_birth);
  }

  const policyholder = householdPolicyholder
    ? formatMember(householdPolicyholder, memberConfig, drugDetails, income)
    : state.get('policyholder').toJS();

  const householdSpouse = members.find((member) => !member.policyholder && !member.dependant);

  let spouseCovered = true;
  if (householdSpouse && jvpUser?.dependents) {
    const jvpSpouse = jvpUser.dependents.find(
      (dependent: JVPDependent) =>
        dependent.relationship === 'spouse' || dependent.relationship === 'partner',
    );

    if (jvpSpouse) {
      householdSpouse.gender = jvpGenderToCommApiGender(jvpSpouse.sex_at_birth);
      if (jvpSpouse.relationship === 'spouse') {
        householdSpouse.member_type = MEMBER_TYPE.SPOUSE;
      }
      // Alex ID return users with a domestic partner will have relationship set but partners sending demographic data will only have marital_status set, so we check for both here for the prefill
      else if (jvpSpouse.relationship === 'partner' || jvpUser.marital_status === 'domestic_partner') {
        householdSpouse.member_type = MEMBER_TYPE.DOMESTIC_PARTNER;
      } else householdSpouse.member_type = MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER;
    }

    spouseCovered = !explicitlyUncoveredMemberIds.includes(stripSourceComponent(householdSpouse.external_id));
  }
  const spouse = householdSpouse
    ? formatMember(householdSpouse, memberConfig, drugDetails, null, spouseCovered)
    : state.get('spouse').toJS();

  const householdDependents = members.filter((member) => member.dependant);

  let dependents =
    householdDependents.length > 0
      ? householdDependents.map((dependent) => {
          const newDependent = dependent;
          let dependentCovered = true;

          if (jvpUser?.dependents) {
            const jvpDependent = jvpUser.dependents.find((i) => i.external_id === dependent.external_id);
            if (jvpDependent) {
              newDependent.gender = jvpGenderToCommApiGender(jvpDependent.sex_at_birth);
            }

            dependentCovered = !explicitlyUncoveredMemberIds.includes(
              stripSourceComponent(dependent.external_id),
            );
          }
          return formatMember(newDependent, memberConfig, drugDetails, null, dependentCovered);
        })
      : state.get('dependents').toJS();
  if (!dependents?.some((dep) => dep?.isValid === false && dep?.isComplete === false)) {
    dependents = [...dependents, { ...newMemberState(MEMBER_TYPE.CHILD) }];
  }

  const surveyObj: Record<string, string> = survey.reduce(
    (acc, i) => ({
      [SurveyQuestionMap[i.question_id]]: i.answer || '',
      ...acc,
    }),
    {},
  );

  // Answers the household carries that aren't one of the fixed profile questions are
  // the survey-question answers, keyed by the id comm-api asked under. Restoring them
  // is what stops a returning member being shown blank questions they already answered.
  // Ids no longer asked are dropped when the question list arrives.
  const networkPreferenceSurveyAnswers: Record<string, string> = survey.reduce(
    (acc, i) => (SurveyQuestionMap[i.question_id] ? acc : { ...acc, [i.question_id]: i.answer }),
    {},
  );

  const hiddenQuestions: {
    eligibility: string[];
    location: string[];
  } = {
    eligibility: hiddenEligibilityQuestions,
    location: hiddenLocationQuestions,
  };

  /**
   * prefer mergeDeep here because we don't want to overwrite some existing data
   * that may exist. For example, if clientSurveyResponses has the _other_
   * publicationKey set, we don't intend to replace it.
   */
  let newState = state.mergeDeepRaw({
    clientSurveyResponses: fromJS(clientSurveyResponses),
    zipcode: zipCode,
    state_code: stateCode,
    hsa_funds: fromJS(hsaFundsData),
    policyholder: fromJS(policyholder),
    spouse: fromJS(spouse),
    dependents: fromJS(dependents),
    survey: {
      risk_question_1: surveyObj?.risk_question_1 || '',
      risk_question_2: surveyObj?.risk_question_2 || '',
      capacity_to_pay: surveyObj?.capacity_to_pay || '',
    },
  });

  newState = newState.setRaw('hiddenQuestions', hiddenQuestions).setRaw('prefillHasCompleted', true);

  // setRaw: loading a household is not a user edit, so it must not queue another save.
  if (Object.keys(networkPreferenceSurveyAnswers).length > 0) {
    newState = newState.setRaw('networkPreferenceSurveyAnswers', fromJS(networkPreferenceSurveyAnswers));
  }

  return newState;
}

function getClientSurvey(state: HouseholdTrackingProfileState) {
  return state.set('clientSurveyIsLoading', true);
}

function getClientSurveySuccess(
  state: HouseholdTrackingProfileState,
  action: { response: ClientSurveySchema },
) {
  const { response } = action;

  const defaultPublication = 'upcoming';
  const clientSurveyResponses = { [defaultPublication]: {} };

  if (response[defaultPublication].properties) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(response[defaultPublication].properties)) {
      clientSurveyResponses[defaultPublication][key] = '';
    }
  }
  return state
    .set('clientSurvey', fromJS(response))
    .set('clientSurveyResponses', fromJS(clientSurveyResponses))
    .set('clientSurveyIsLoading', false)
    .set('clientSurveyIsLoaded', true);
}

function getClientSurveyFailure(
  state: HouseholdTrackingProfileState,
  action: {
    error: Error;
  },
) {
  return state.set('clientSurveyError', action.error).set('clientSurveyIsLoading', false);
}

function getJvpClientSurvey(state: HouseholdTrackingProfileState) {
  return state.set('clientSurveyIsLoading', true);
}

function getJvpClientSurveySuccess(
  state: HouseholdTrackingProfileState,
  action: {
    response: Record<'active' | 'upcoming', ClientSurveySchema>;
  },
) {
  const { response } = action;

  // Dynamically create keys in survey responses field
  // The responses get reset every time the action gets called, so the survey should only be retrieve once
  const clientSurveyResponses = {
    active: {},
    upcoming: {},
  };

  if (response.active && response.active.properties) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(response.active.properties)) {
      // key is jv_eligibility_question_id from bootstrap response state
      clientSurveyResponses.active[key] = '';
    }
  }

  if (response.upcoming && response.upcoming.properties) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(response.upcoming.properties)) {
      // key is jv_eligibility_question_id from bootstrap response state
      clientSurveyResponses.upcoming[key] = '';
    }
  }

  return state
    .set('clientSurvey', fromJS(response))
    .set('clientSurveyResponses', fromJS(clientSurveyResponses))
    .set('clientSurveyIsLoading', false)
    .set('clientSurveyIsLoaded', true);
}

function getJvpClientSurveyFailure(
  state: HouseholdTrackingProfileState,
  action: {
    error: Error;
  },
) {
  return state.set('clientSurveyError', action.error).set('clientSurveyIsLoading', false);
}

function submitClientSurvey(
  state: HouseholdTrackingProfileState,
  action: {
    responses: ClientSurveyResponsesByYear;
  },
) {
  return state.set('clientSurveyResponses', fromJS(action.responses));
}

function getJvpEligibilityQuestionsSuccess(
  state: HouseholdTrackingProfileState,
  action: {
    response: JVPEligibilityQuestions;
  },
) {
  return state
    .set('jvpEligibilityQuestions', fromJS(action.response))
    .set('jvpEligibilityQuestionsLoading', false);
}

function getJvpEligibilityQuestionsFailure(
  state: HouseholdTrackingProfileState,
  action: {
    error: Error;
  },
) {
  return state
    .set('jvpEligibilityQuestionsError', fromJS(action.error))
    .set('jvpEligibilityQuestionsLoading', false);
}

function submitIncentiveSurvey(
  state: HouseholdTrackingProfileState,
  action: {
    responses: Record<string, IncentiveSurveyAnswer | null>;
  },
) {
  return state.set('incentiveSurveyAnswers', fromJS(action.responses));
}

function submitResultIncentiveSurvey(
  state: HouseholdTrackingProfileState,
  action: {
    responses: Record<string, IncentiveSurveyAnswer | null>;
  },
) {
  const resultPageIncentiveSurvey = {
    ...state.get('resultPageIncentiveSurvey').toJS(),
    incentiveSurveyAnswers: action.responses,
  };
  return state.set('resultPageIncentiveSurvey', resultPageIncentiveSurvey);
}

/**
 * Helper function to determine if any household member could become pregnant
 * Criteria: female (or prefer not to answer/unknown gender) AND age >= 12
 */
function canHouseholdBecomePregnant(members: Member[]): boolean {
  return members.some((m) => {
    const age = m.age;
    if (typeof age === 'string') {
      const numericAge = parseInt(age, 10);
      return (
        (m.gender === 'female' || m.gender === 'prefer_not_to_answer' || m.gender === '') &&
        !Number.isNaN(numericAge) &&
        numericAge >= 12
      );
    }
    return (m.gender === 'female' || m.gender === 'prefer_not_to_answer' || m.gender === '') && age >= 12;
  });
}

/**
 * Helper function to check if member is age >= 63
 * Used to determine if we should ask about medicare enrollment
 */
function isMemberAgeSixtyThreeOrOlder(age: string | number | undefined | null): boolean {
  if (age === undefined || age === null || age === '') return false;
  const numericAge = typeof age === 'number' ? age : parseInt(age as string, 10);
  return !Number.isNaN(numericAge) && numericAge >= 63;
}

/**
 * Helper function to reset pregnancy question if no one in household could become pregnant
 * Returns updated state if reset occurred, otherwise returns original state.
 */
function resetPregnancyIfNeeded(
  state: HouseholdTrackingProfileState,
  oldMembers: Member[],
  newMembers: Member[],
): HouseholdTrackingProfileState {
  const oldCanBecomePregnant = canHouseholdBecomePregnant(oldMembers);
  const newCanBecomePregnant = canHouseholdBecomePregnant(newMembers);

  // Reset pregnancy answer if criteria changed from true to false
  if (oldCanBecomePregnant && !newCanBecomePregnant) {
    return state.setIn(['survey', 'plan_child_question'], '');
  }

  return state;
}

function editPolicyholder(
  state: HouseholdTrackingProfileState,
  action: {
    fields: UpdateMemberFields;
    memberConfig?: MemberSectionConfigFields;
  },
) {
  const policyholder = state.get('policyholder').toJS();
  const spouse = state.get('spouse').toJS();
  const dependents = state.get('dependents').toJS();

  // Update each key in policyholder
  const newPolicyholder = {
    ...policyholder,
    ...action.fields,
  };

  newPolicyholder.isValid = validateMember(newPolicyholder, action.memberConfig);
  if (newPolicyholder.isValid) {
    newPolicyholder.isComplete = true;
  }

  // Check if medicare eligibility changed for policyholder
  if ('age' in action.fields) {
    const oldEligible = isMemberAgeSixtyThreeOrOlder(policyholder.age);
    const newEligible = isMemberAgeSixtyThreeOrOlder(newPolicyholder.age);

    // Reset on_medicare if policyholder no longer eligible
    if (oldEligible && !newEligible && newPolicyholder.on_medicare !== null) {
      newPolicyholder.on_medicare = null;
    }
  }

  let newState = state.set('policyholder', fromJS(newPolicyholder));

  // Check if pregnancy criteria changed (gender or age affecting canBecomePregnant)
  if ('gender' in action.fields || 'age' in action.fields) {
    newState = resetPregnancyIfNeeded(
      newState,
      [policyholder, spouse, ...dependents],
      [newPolicyholder, spouse, ...dependents],
    );
  }

  return newState;
}

function editSpouse(
  state: HouseholdTrackingProfileState,
  action: {
    fields: UpdateMemberFields;
    memberConfig: MemberSectionConfigFields;
  },
) {
  const policyholder = state.get('policyholder').toJS();
  const oldSpouse = state.get('spouse').toJS();
  const dependents = state.get('dependents').toJS();

  // Update each key in spouse
  const spouse = {
    ...oldSpouse,
    ...action.fields,
  };

  spouse.isValid = validateMember(spouse, action.memberConfig);
  if (spouse.isValid) {
    spouse.isComplete = true;
  }

  let newState = state.set('spouse', fromJS(spouse));

  // Check if pregnancy criteria changed (gender or age affecting canBecomePregnant)
  if ('gender' in action.fields || 'age' in action.fields) {
    newState = resetPregnancyIfNeeded(
      newState,
      [policyholder, oldSpouse, ...dependents],
      [policyholder, spouse, ...dependents],
    );
  }

  return newState;
}

function removeSpouse(state: HouseholdTrackingProfileState) {
  const policyholder = state.get('policyholder').toJS();
  const oldSpouse = state.get('spouse').toJS();
  const dependents = state.get('dependents').toJS();

  // Keep original UUID
  const externalId = state.getIn(['spouse', 'external_id']);
  const spouse = {
    ...newMemberStateWithExistingExternalId(MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER, externalId),
  };

  let newState = state.set('spouse', fromJS(spouse));

  // Check if pregnancy criteria changed after removing spouse
  newState = resetPregnancyIfNeeded(
    newState,
    [policyholder, oldSpouse, ...dependents],
    [policyholder, spouse, ...dependents],
  );

  return newState;
}

function editDependent(
  state: HouseholdTrackingProfileState,
  action: {
    memberId: string;
    fields: UpdateMemberFields;
    memberConfig: MemberSectionConfigFields;
  },
) {
  const policyholder = state.get('policyholder').toJS();
  const spouse = state.get('spouse').toJS();
  const oldDependents = state.get('dependents').toJS();
  const dependents = [...oldDependents];
  const index = dependents.findIndex((i) => i.external_id === action.memberId);

  dependents[index] = {
    ...dependents[index],
    ...action.fields,
  };

  dependents[index].isValid = validateMember(dependents[index], action.memberConfig);
  if (dependents[index].isValid) {
    dependents[index].isComplete = true;
  }

  // Push an new dependent onto list in order to render an additional Add Dependent button
  if (index + 1 === dependents.length) {
    dependents.push(newMemberState(MEMBER_TYPE.CHILD));
  }

  let newState = state.set('dependents', fromJS(dependents));

  // Check if pregnancy criteria changed (gender or age affecting canBecomePregnant)
  if ('gender' in action.fields || 'age' in action.fields) {
    newState = resetPregnancyIfNeeded(
      newState,
      [policyholder, spouse, ...oldDependents],
      [policyholder, spouse, ...dependents],
    );
  }

  return newState;
}

function removeDependent(
  state: HouseholdTrackingProfileState,
  action: {
    memberId: string;
  },
) {
  const policyholder = state.get('policyholder').toJS();
  const spouse = state.get('spouse').toJS();
  const oldDependents = state.get('dependents').toJS();

  const updatedDependents = oldDependents.filter((i) => i.external_id !== action.memberId);

  let newState = state.set('dependents', fromJS(updatedDependents));

  // Check if pregnancy criteria changed after removing dependent
  newState = resetPregnancyIfNeeded(
    newState,
    [policyholder, spouse, ...oldDependents],
    [policyholder, spouse, ...updatedDependents],
  );

  return newState;
}

function changeFormValue(
  state: HouseholdTrackingProfileState,
  action: {
    name: keyof IProfilePageReducerState;
    value: string;
  },
) {
  return state.set(action.name, action.value);
}

function changeSurveyValue(
  state: HouseholdTrackingProfileState,
  action: {
    name: keyof Survey;
    value: RiskAssessmentAnswer | CapacityToPayAnswer | 'yes' | 'no' | '';
  },
) {
  return state.setIn(['survey', action.name], action.value);
}

function searchLocations(state: HouseholdTrackingProfileState) {
  return state.set('searchIsLoading', true).set('countyResults', fromJS([]));
}

function searchLocationsSuccess(
  state: HouseholdTrackingProfileState,
  action: {
    response: GetLocationsResponse;
  },
) {
  let stateCode = state.get('state_code');
  const res = action.response.locations;

  if (res.length >= 1) {
    stateCode = res[0].state as string;
  }

  if (res.length === 0) {
    stateCode = '';
  }

  return state.set('searchIsLoading', false).set('countyResults', fromJS(res)).set('state_code', stateCode);
}

function searchLocationsFailure(
  state: HouseholdTrackingProfileState,
  action: {
    error: Error;
  },
) {
  return state.set('searchIsLoading', false).set('searchError', action.error);
}

function resetCounties(state: HouseholdTrackingProfileState) {
  return state.set('countyResults', fromJS([]));
}

function searchDrugs(
  state: HouseholdTrackingProfileState,
  action: {
    query: string;
  },
) {
  return state.set('rxQuery', action.query).set('rxIsLoading', true);
}

function searchDrugsSuccess(
  state: HouseholdTrackingProfileState,
  action: {
    response: GetDrugsResponse;
  },
) {
  return state.set('rxResults', fromJS(action.response.drugs)).set('rxIsLoading', false);
}

function searchDrugsFailure(
  state: HouseholdTrackingProfileState,
  action: {
    error: Error;
  },
) {
  return state.set('rxError', fromJS(action.error)).set('rxIsLoading', false);
}

// clear prescription/utilization if answer is 'no'
function sanitizeProfileInputs(state: HouseholdTrackingProfileState) {
  let policyholder = state.get('policyholder').toJS();
  let spouse = state.get('spouse').toJS();
  let dependents = state.get('dependents').toJS();

  let planChildQuestion = state.getIn(['survey', 'plan_child_question']);

  const canBecomePregnant =
    dependents
      .concat([policyholder, spouse])
      .filter(
        (member) =>
          (member.gender === 'female' || member.gender === 'prefer_not_to_answer' || member.gender === '') &&
          member.age >= 12,
      ).length > 0;

  if (!canBecomePregnant) {
    planChildQuestion = '';
  }

  const emptyUtilization = {
    pcp_visits: 0,
    specialist_visits: 0,
    inpatient_days: 0,
  };

  // clear policyholder prescription/utilization if user answered 'no'
  if (!policyholder.uses_prescriptions) {
    policyholder = {
      ...policyholder,
      prescriptions: [],
    };
  }
  if (!policyholder.uses_utilization) {
    policyholder = {
      ...policyholder,
      utilization: emptyUtilization,
    };
  }

  // clear spouse prescription/utilization if user answered 'no'
  if (spouse.isValid) {
    if (!spouse.uses_prescriptions) {
      spouse = {
        ...spouse,
        prescriptions: [],
      };
    }
    if (!spouse.uses_utilization) {
      spouse = {
        ...spouse,
        utilization: emptyUtilization,
      };
    }
  }

  // clear dependent prescription/utilization if user answered 'no'
  dependents = dependents.map((dependent) => {
    let sanitized = { ...dependent };
    if (dependent.isValid) {
      if (!dependent.uses_prescriptions) {
        sanitized = {
          ...sanitized,
          prescriptions: [],
        };
      }
      if (!dependent.uses_utilization) {
        sanitized = {
          ...sanitized,
          utilization: emptyUtilization,
        };
      }
    }
    return sanitized;
  });

  return state
    .setRaw('policyholder', fromJS(policyholder))
    .setRaw('spouse', fromJS(spouse))
    .setRaw('dependents', fromJS(dependents))
    .setRawIn(['survey', 'plan_child_question'], fromJS(planChildQuestion));
}

function setSelectedMemberIds(
  state: HouseholdTrackingProfileState,
  action: {
    memberIds: string[];
  },
) {
  let newState = state;

  const spouseExternalId = state.getIn(['spouse', 'external_id']);
  const spouseIsCovered = action.memberIds.includes(spouseExternalId);
  newState = newState.setRawIn(['spouse', 'isCovered'], spouseIsCovered);

  const dependents = state.get('dependents');
  dependents.forEach((dependent, idx) => {
    if (dependent.get('isValid')) {
      const externalId = dependent.get('external_id');
      const isCovered = action.memberIds.includes(externalId);
      newState = newState.setRawIn(['dependents', idx, 'isCovered'], isCovered);
    }
  });

  return newState;
}

function handleGetAAPIUserSuccess(state: HouseholdTrackingProfileState, action: { user: AlexIdUser }) {
  return state
    .setRawIn(['policyholder', 'external_id'], action.user.alex_id_uuid)
    .setRaw('customerProductKey', action.user.customer_product_key);
}

function setIncentiveSurvey(state, action) {
  return state
    .set('incentiveSurvey', action.data.incentiveSurvey)
    .set('incentiveSurveyAnswers', action.data.incentiveSurveyAnswers)
    .set('resultPageIncentiveSurvey', { incentiveSurvey: [], incentiveSurveyAnswers: {} });
}

function getIncentiveSurveySuccess(
  state,
  action: {
    response: IncentiveSurveyResponse;
  },
) {
  const surveyQuestions = action.response.incentive_questions;

  const surveyAnswers = state.get('incentiveSurveyAnswers').toJS();

  // persist answers if they still appear in the new response (i.e., form changes didn't make the user ineligible for the incentive)
  const emptySurveyAnswers = surveyQuestions.reduce(
    (acc, i) => ({
      ...acc,
      [i.id]: surveyAnswers?.[i.id] || null,
    }),
    {},
  );

  return state
    .set('incentiveSurvey', surveyQuestions)
    .set('incentiveSurveyAnswers', emptySurveyAnswers)
    .set('incentiveSurveyIsLoading', false)
    .set('incentiveSurveyAPICalled', true);
}

function getIncentiveSurveyFailure(state, action) {
  return state.set('incentiveSurveyError', action.error).set('incentiveSurveyIsLoading', false);
}

function submitNetworkPreferenceSurvey(
  state,
  action: {
    responses: NetworkPreferenceSurveyAnswers;
  },
) {
  // The answers map always holds one key per asked question (seeded when the list is
  // fetched), so "no nulls left" is the same as "the section is finished".
  const answers = Object.values(action.responses || {});
  const isSectionAnswered = answers.length > 0 && answers.every((answer) => answer != null);

  // Only the answer that completes the section marks the household dirty, so the whole
  // set rides one save instead of one save per question. Answers given along the way are
  // still kept in state and go out with that save; setRaw is what keeps them from
  // triggering their own.
  return isSectionAnswered
    ? state.set('networkPreferenceSurveyAnswers', fromJS(action.responses))
    : state.setRaw('networkPreferenceSurveyAnswers', fromJS(action.responses));
}

function getNetworkPreferenceSurveySuccess(
  state,
  action: {
    response: NetworkPreferenceSurveyResponse;
  },
) {
  const surveyQuestions = action.response.network_preference_questions;

  const surveyAnswers = state.get('networkPreferenceSurveyAnswers').toJS();

  // persist any answers whose question is still asked (profile changes may change the list)
  const emptySurveyAnswers = surveyQuestions.reduce(
    (acc, i) => ({
      ...acc,
      [i.id]: surveyAnswers?.[i.id] || null,
    }),
    {},
  );

  return (
    state
      .set('networkPreferenceSurvey', surveyQuestions)
      // Seeding from the fetched question list is API-driven, not a user edit, so it must
      // NOT mark the household dirty (networkPreferenceSurveyAnswers is a HOUSEHOLD_FIELD).
      // Only submitNetworkPreferenceSurvey (a user answering) uses tracked .set().
      .setRaw('networkPreferenceSurveyAnswers', emptySurveyAnswers)
      .set('networkPreferenceSurveyAPICalled', true)
  );
}

function getNetworkPreferenceSurveyFailure(state, action) {
  return (
    state
      .set('networkPreferenceSurveyError', action.error)
      // Also flip on failure — otherwise a comm-api outage would permanently
      // block "profile complete" for everyone.
      .set('networkPreferenceSurveyAPICalled', true)
  );
}

function setActiveHouseholdMembers(state, action) {
  return state.set('activeMembers', action.members);
}

function setResultPageIncentiveSurvey(
  state,
  action: {
    response: IncentiveSurveyResponse;
  },
) {
  const surveyQuestions = action.response.incentive_questions;

  const surveyAnswers = state.get('incentiveSurveyAnswers').toJS();

  // persist answers if they still appear in the new response (i.e., form changes didn't make the user ineligible for the incentive)
  const emptySurveyAnswers = surveyQuestions.reduce(
    (acc, i) => ({
      ...acc,
      [i.id]: surveyAnswers?.[i.id] || null,
    }),
    {},
  );

  const data = {
    incentiveSurvey: surveyQuestions,
    incentiveSurveyAnswers: emptySurveyAnswers,
  };

  return state.set('resultPageIncentiveSurvey', data).set('incentiveSurveyIsLoading', false);
}

function profilePageReducerWithHouseholdFlag(state = initialProfileState, action): unknown {
  const tracked = new HouseholdTrackingProfileState(state);
  const result = profilePageReducer(tracked, action);
  return result.unwrap();
}

const pojoProfilePageReducer = wrapImmutableReducerInPojoTranslation(profilePageReducerWithHouseholdFlag);

export default pojoProfilePageReducer;
